import { FC, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Plus } from 'react-feather'
import produce from 'immer'
import {
  useQueryParam,
  useQueryParams,
  JsonParam,
  NumberParam,
  withDefault,
  StringParam,
} from 'use-query-params'
import {
  usePaginatedProjects,
  useAuthState,
  useGlobalState,
  useDebounceState,
  Spin,
  useCompanyDetails,
  Modal,
} from '@pepper/ui'
import noop from '@pepper/utils/src/function/noop'
import { dataLayerProjectCreationInitiateEvent } from '@pepper/utils/src/function/dataLayerEvents'
import { projectSort, draftSort } from '@pepper/utils/src/constant/enum/sort'

import {
  checkIsValidCompanyId,
  useSelectedCompanyId,
} from 'utils/functions/misc'
import { getDefaultItemType } from 'utils/functions/getDefaultItemType'
import { usePaginatedDraftsList } from 'utils/hooks/usePaginatedDraftsList'
import { useProjectDomainMap } from 'utils/hooks/useProjectDomainMap'
import ProjectHorizontalCard from 'components/Card/ProjectHorizontalCard'
import ProjectGridCard from 'components/Card/ProjectGridCard'
import CreateProject from 'components/CreateProject'
import CreateOrganisationJourney from 'container/CreateOrganisationJourney/CreateOrganisationJourney'

import Header from './Header'
import { Footer } from './Footer'
import EmptyState from './EmptyState'
import { statusFilter } from './constants/StatusBasedFilter'
import styles from './_.module.css'

export type View = 'list' | 'grid'
const PROJECT_PER_PAGE = 10

const LiveProjectsList: FC = () => {
  const { user } = useAuthState()
  const companyId = useSelectedCompanyId()
  const isValidCompanyId = checkIsValidCompanyId(companyId, user)
  const {
    setPendingSidebarCountKeys,
    setTopbarComponent,
    viewport,
    pendingSidebarCountKeys,
    permissions,
  } = useGlobalState()
  const history: any = useHistory()
  const [activeProjectsTab] = useQueryParam('projectList', StringParam)
  const location = useLocation()
  const isMobileView = viewport === 'md'
  const [projectType, setProjectType] = useState<string>(
    history.location?.state?.projectType || activeProjectsTab
  )
  const [showDataView, setShowDataView] = useState<View>(
    isMobileView ? 'grid' : 'list'
  )
  const [searchValue, setSearchValue] = useState<string>('')
  const [modal, setModal] = useState(false)
  const [createModal, setCreateModal] = useState(false)
  const [showProjectModal, setShowProjectModal] = useState(false)
  const debouncedSearchValue = useDebounceState<string>(searchValue, 800)

  const [queryParamFilters, setQueryParamFilters] = useQueryParams({
    filters: withDefault(JsonParam, { status: statusFilter['all'].status }),
    sort: withDefault(JsonParam, { sort_order: undefined, sort_by: undefined }),
    currentPage: withDefault(NumberParam, 0),
    projectList: withDefault(StringParam, 'all'),
    limit: withDefault(NumberParam, PROJECT_PER_PAGE),
  })
  const isDraftsTabActive = projectType === 'drafts'

  const { projects, totalProjects, isProjectsLoading, refetchProjects } =
    usePaginatedProjects(
      {
        current_page: queryParamFilters.currentPage,
        ...queryParamFilters.filters,
        ...queryParamFilters.sort,
        company_id: companyId,
        search_title: debouncedSearchValue,
        page_size: queryParamFilters.limit,
        pagination_enabled: true,
      },
      { enabled: Boolean(companyId) && isValidCompanyId && !isDraftsTabActive }
    )

  const {
    records: draftRecords,
    totalRecords: draftTotalRecords,
    refetch: refetchDrafts,
    isLoading: isDraftsLoading,
  } = usePaginatedDraftsList(
    {
      ...queryParamFilters.filters,
      search_title: debouncedSearchValue,
      ...queryParamFilters.sort,
      company_id: companyId,
    },
    queryParamFilters.limit,
    queryParamFilters.currentPage,
    isDraftsTabActive
  )

  const { data: company } = useCompanyDetails(
    `/companies/${companyId}`,
    companyId,
    true
  )

  const { domainMap } = useProjectDomainMap({
    companyId,
  })

  const handleProjectCardClick = (record: Project | ProjectTemplateList) => {
    const tab = permissions?.marketplace ? 'items' : 'topics'

    const search =
      tab === 'items'
        ? getDefaultItemType(!!permissions?.marketplace)
        : undefined

    history.push({
      pathname: `/project/${record.id}/${tab}`,
      search,
    })
  }

  useEffect(() => {
    if (debouncedSearchValue) {
      setQueryParamFilters({
        ...queryParamFilters,
        currentPage: 0,
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue])

  useEffect(() => {
    if (pendingSidebarCountKeys.includes('allProjectsCount')) {
      refetchProjects()
      refetchDrafts()
      setPendingSidebarCountKeys(['allProjectsCount'], 'remove')
    }
  }, [
    pendingSidebarCountKeys,
    refetchProjects,
    refetchDrafts,
    setPendingSidebarCountKeys,
  ])

  useEffect(() => {
    if (projectType && projectType !== 'draft') {
      setQueryParamFilters({
        ...queryParamFilters,
        filters: { status: statusFilter[projectType]?.status },
        projectList: projectType,
        currentPage: 0,
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectType])

  useEffect(() => {
    if (projectType) {
      setQueryParamFilters({
        currentPage: 0,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectType])

  useEffect(() => {
    if (isMobileView) {
      setShowDataView('grid')
    }

    setTopbarComponent(
      <div className={styles.projectHeader}>Projects & Items</div>
    )
    return () => {
      setTopbarComponent(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalProjects, isMobileView])

  /** Handle pre-filled search and status filter if user search project from navbar */
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const _search_title = params.get('search_title')
    const _status = params.get('status')

    /** handle preSelect search title  */
    if (
      _search_title &&
      (queryParamFilters.filters.search_title === '' ||
        !queryParamFilters.filters.search_title)
    ) {
      setSearchValue(_search_title)
    }

    /** handle preSelect status */
    if (
      _status &&
      _status !== 'all' &&
      (queryParamFilters.filters.status === '' ||
        !queryParamFilters.filters.status)
    ) {
      const nextFilters = produce(queryParamFilters, draftState => {
        draftState.filters.status = [_status as ProjectStatus]
      })
      setQueryParamFilters(nextFilters)
      setSearchValue('')
    }
  }, [
    location.search,
    queryParamFilters.filters,
    history,
    queryParamFilters,
    setQueryParamFilters,
  ])

  const handleSaveSkip = () => {
    localStorage.setItem(`${company?.id}_skipPrefill`, JSON.stringify(true))
  }

  const handleStatusChange = (value: string) => {
    setProjectType(value)
  }

  const handleSelectedDraft = (record: Project | ProjectTemplateList): void => {
    history.push({
      pathname: '/create-project-flow',
      search: `?draft_id=${record.id}`,
    })
  }

  const handleRowClick = (project: any) => {
    if (isDraftsTabActive) {
      handleSelectedDraft(project)
    } else {
      handleProjectCardClick(project)
    }
  }

  const handleCreateProject = () => {
    setShowProjectModal(true)
    dataLayerProjectCreationInitiateEvent()
  }

  const _getDomain = (projectId: string) => {
    return domainMap?.[projectId]?.[0]?.domain
  }

  const _getTopicsCount = (projectId: string) => {
    return domainMap?.[projectId]?.[0].total_topics
  }

  useEffect(() => {
    if (history.location.state?.projectType) {
      setProjectType(history.location.state?.projectType)
    } else if (activeProjectsTab) {
      setProjectType(activeProjectsTab)
    }
  }, [history.location.state?.projectType, activeProjectsTab])

  const records = isDraftsTabActive ? draftRecords : projects

  const allProjects = () => {
    return (
      <>
        {showDataView === 'grid' ? (
          <div className={styles.liveProjectCardBlock}>
            {records?.map((project: any, index) => {
              const domain = _getDomain(project.id)
              return (
                <div
                  key={index}
                  onClick={() => handleRowClick(project)}
                  onKeyPress={noop}
                  role="button"
                  tabIndex={0}
                  className={styles.projectCardContainer}
                >
                  <ProjectGridCard
                    project={project}
                    stage_count={project.stage_count ?? {}}
                    has_pending_draft_items={project.has_pending_draft_items}
                    isDraft={isDraftsTabActive}
                    isPaidDraft={project.payment_status === 'payment.captured'}
                    domain={domain}
                    number_of_items={project.number_of_items}
                    topicsCount={_getTopicsCount(project.id)}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          records?.map((project: any, index) => {
            const domain = _getDomain(project.id)
            return (
              <div
                key={index}
                onClick={() => handleRowClick(project)}
                onKeyPress={noop}
                role="button"
                tabIndex={0}
                className={styles.projectCardContainer}
              >
                <ProjectHorizontalCard
                  key={project.id}
                  project={project}
                  stage_count={project.stage_count ?? {}}
                  isDraft={isDraftsTabActive}
                  domain={domain}
                  topicsCount={_getTopicsCount(project.id)}
                />
              </div>
            )
          })
        )}
        <Footer
          total={isDraftsTabActive ? draftTotalRecords : totalProjects}
          queryParamFilters={queryParamFilters}
          type="projects"
          setQueryParamFilters={setQueryParamFilters}
        />
      </>
    )
  }

  const showLoader = isDraftsTabActive ? isDraftsLoading : isProjectsLoading

  const hasRecords = isDraftsTabActive
    ? draftTotalRecords > 0
    : projects.length > 0

  const projectsToDisplay = hasRecords ? (
    allProjects()
  ) : (
    <EmptyState onCreate={handleCreateProject} />
  )

  useEffect(() => {
    const hasSortOption = Object.values(draftSort).some(
      option => option.sort_by === queryParamFilters.sort.sort_by
    )

    if (activeProjectsTab === 'drafts' && !hasSortOption) {
      setQueryParamFilters({
        ...queryParamFilters,
        sort: {
          sort_order: null,
          sort_by: null,
        },
      })
    }
  }, [activeProjectsTab, queryParamFilters, setQueryParamFilters])

  useEffect(() => {
    setQueryParamFilters({ limit: queryParamFilters.limit })
  }, [])

  return (
    <>
      <Header
        tabName="project"
        setShowDataView={setShowDataView}
        showDataView={showDataView}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        sortOptions={isDraftsTabActive ? draftSort : projectSort}
        handleStatusChange={handleStatusChange}
        projectType={projectType}
        isDisabled={!hasRecords}
        onCreate={handleCreateProject}
      />

      {showLoader ? (
        <Spin className={styles.spin} size="large" />
      ) : (
        projectsToDisplay
      )}
      <Modal
        visible={modal}
        onCancel={() => {
          setModal(false)
          handleSaveSkip()
        }}
        maskClosable={false}
        cancelText="Skip"
        okText="Add Details"
        className={styles.modal}
        width={800}
        cancelButtonProps={{ type: 'link' }}
        onOk={() => {
          setModal(false)
          setCreateModal(true)
        }}
        okButtonProps={{ icon: <Plus size={12} style={{ marginRight: 5 }} /> }}
      >
        <div>
          <p className={styles.title}>We&apos;ve got something for you!</p>
          <p>
            We want to get to know you better to provide you the best
            experience! For that, we need your help.
          </p>
        </div>
      </Modal>
      <CreateOrganisationJourney
        modal={createModal}
        setModal={setCreateModal}
        id={companyId}
        companyInfo={{
          organisationName: company?.company_name ?? '',
          organisationWebsite: company?.company_website ?? '',
        }}
      />
      <CreateProject
        companyId={companyId}
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
      />
    </>
  )
}

export default LiveProjectsList
